import React, { useEffect, useRef, useState } from 'react';
import { isEmpty } from 'validate.js';

import {
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEWS
} from 'dpl/constants/analytics';
import TransitionMenu from 'dpl/common/components/TransitionMenu';
import BreedSearch from 'dpl/components/BreedSearch';
import LocationAutocompleteContainer from 'dpl/search/containers/LocationAutocompleteContainer';
import LocationResult from 'dpl/search_result_filters/components/UserLocation/LocationResult';
import { addQueryToUrl, addToQuery } from 'dpl/util/queryString';
import {
  FILTER_LATITUDE,
  FILTER_LOCATION_NAME,
  FILTER_LONGITUDE,
  LOCATION_REDIRECT
} from 'dpl/constants/query_params';
import Icon from 'dpl/common/components/Icon';
import {
  ORDERED_SEARCH_TYPE_VALUES,
  ORDERED_SEARCH_TYPES
} from 'dpl/shared/constants/shared';
import { BREED_TYPES } from 'dpl/components/BreedSearch/util';

import SheltersSearch from './SheltersSearch';

export default function HomepageSearchBar() {
  const searchFormRef = useRef(null);
  const locationInputRef = useRef(null);

  const [selectedSearchType, setSelectedSearchType] = useState(
    ORDERED_SEARCH_TYPES.BREEDERS
  );

  const [isBreedDropdownOpen, setIsBreedDropdownOpen] = useState(
    window.__DPL_BOT_UA
  );
  const [selectedBreed, setSelectedBreed] = useState({});

  const [locationSearchValue, setLocationSearchValue] = useState('');
  const [selectedLocation, setSelectedLocation] = useState({});

  function handleMenuItemClick(e, idx) {
    e.preventDefault();
    setSelectedSearchType(ORDERED_SEARCH_TYPE_VALUES[idx]);
  }

  function handleBreedDropdownChange() {
    setIsBreedDropdownOpen(isBreedDropdownOpen);
  }

  function handleBreedSelect(breed) {
    setSelectedBreed({
      name: breed.name,
      slug: `${breed.url}`
    });
    locationInputRef?.current?.focus();
  }

  function handleLocationInputChange(e) {
    setLocationSearchValue(e.target.value);

    if (e.target.value === '') {
      setSelectedLocation({});
    }
  }

  async function handleLocationSelect(location) {
    const coordinates = await location.getCoordinates();

    setLocationSearchValue(location.name);
    setSelectedLocation({
      name: location.name,
      coords: [coordinates.lat, coordinates.lng]
    });
  }

  useEffect(() => {
    let timer;

    if (!isEmpty(selectedBreed) && !isEmpty(selectedLocation)) {
      timer = setTimeout(() => {
        searchFormRef.current?.requestSubmit();
      }, 200);
    }

    return () => clearTimeout(timer);
  }, [selectedBreed, selectedLocation]);

  function handleSubmit(e) {
    e.preventDefault();

    let searchUrl = addQueryToUrl(
      {
        origin_view: ANALYTICS_VIEWS.HOME,
        origin_view_feature: ANALYTICS_VIEW_FEATURES.SEARCH_HERO
      },
      selectedBreed.slug
    );

    if (!isEmpty(selectedLocation)) {
      const locationParams = {
        [FILTER_LOCATION_NAME]: selectedLocation.name,
        [FILTER_LATITUDE]: selectedLocation.coords[0],
        [FILTER_LONGITUDE]: selectedLocation.coords[1]
      };
      searchUrl = `${addQueryToUrl(
        { [LOCATION_REDIRECT]: true },
        searchUrl
      )}#${addToQuery(locationParams)}`;
    }

    window.location.assign(searchUrl);
  }

  const isBreederSearch = ORDERED_SEARCH_TYPES.BREEDERS === selectedSearchType;
  const isShelterSearch = ORDERED_SEARCH_TYPES.SHELTERS === selectedSearchType;

  return (
    <div className="HomepageSearchBar container-xl">
      <div className="ba b--default br12 bg-white">
        <div className="pt6 ph6 bb b--default">
          <TransitionMenu
            items={ORDERED_SEARCH_TYPE_VALUES}
            selectedIdx={ORDERED_SEARCH_TYPE_VALUES.indexOf(selectedSearchType)}
            onItemClick={handleMenuItemClick}
            itemClassName="font-16 fw-medium pb3"
            itemPadding={12}
            customColors={{
              itemColorClassName: 'tertiary',
              borderColorClassName: 'bg-default',
              selectedColorClassName: 'default-color'
            }}
          />
        </div>

        {isBreederSearch && (
          <form
            ref={searchFormRef}
            className="grid grid-cols-2"
            onSubmit={handleSubmit}
          >
            <BreedSearch
              className="HomepageSearchBar__breeds font-18 font-serif bn br12 br--bottom pl6 pr4 pv6 w-100"
              placeholder="Search a breed"
              isOpen={isBreedDropdownOpen}
              onChange={handleBreedDropdownChange}
              query={selectedBreed.name}
              onSelectItem={handleBreedSelect}
              breedType={BREED_TYPES.ALL}
              isNewLayout
            />
            <div className="bl b--default relative">
              <LocationAutocompleteContainer
                classes={{
                  menu: 'br3 dropdown-box-shadow mt4',
                  selectedMenuItem: 'bg-subtle-gray br2'
                }}
                inputProps={{
                  name: 'search-location',
                  type: 'text',
                  ref: locationInputRef,
                  className: 'bn br12 pl4 pr13 pv6 w-100 font-18 font-serif',
                  onChange: handleLocationInputChange,
                  placeholder: 'Add your location'
                }}
                menuCloseTimeout={300}
                onSelect={handleLocationSelect}
                query={locationSearchValue}
                ResultsItemComponent={LocationResult}
                resultsItemProps={{
                  classes: {
                    wrapper: 'fw-medium ph4 pv3',
                    icon: 'pv3 ph3'
                  },
                  iconSize: '24px'
                }}
                stripCountryCode
              />
              <button
                type="submit"
                className="absolute top-0 right-0 bottom-0 mr6 primary hover-stone"
                disabled={isEmpty(selectedBreed)}
              >
                <Icon name="fetch-search" height="20px" width="20px" />
              </button>
            </div>
          </form>
        )}
        {isShelterSearch && <SheltersSearch />}
      </div>
    </div>
  );
}
